/**
 * Audit trail for the staff-only `/admin` portal (SUPENG-220).
 *
 * Unlike the customer-facing feature modules, the filter axis here is the admin
 * who acted, not `property_id` — so every event carries the author identity
 * (`admin_user_id` / `admin_email` / `admin_username`) and an explicit
 * `occurred_at`. That identity is deliberate: impersonation, account deletion
 * and email changes are only auditable if we know who performed them. See
 * `docs/OBSERVABILITY.md` for the reasoning behind that exception.
 *
 * Filter everything this module emits in Sentry with `feature:admin`.
 */

import {
  getErrorMessage,
  getHttpStatus,
  isNetworkFailure,
  logFeatureEvent,
  reportFeatureIssue,
} from "./shared";
import { getAdminAuditSessionId, recordAdminAudit } from "./adminAudit";

/** The signed-in admin performing the action, plus the basic-auth identity they typed. */
export type AdminAuthor = {
  userId?: string | null;
  email?: string | null;
  name?: string | null;
  adminUsername?: string | null;
};

/**
 * Audited actions, grouped by the surface they come from. The union doubles as
 * the filter schema — `admin_event:impersonation_link_created` has to keep
 * meaning the same thing over time.
 */
type AdminEvent =
  // Access + login activity
  | "portal_opened"
  | "portal_login_submitted"
  | "access_denied"
  | "portal_exited"
  | "tab_viewed"
  // Member access
  | "user_search_performed"
  | "user_health_viewed"
  // Impersonation
  | "impersonation_session_started"
  | "impersonation_link_created"
  // Operations
  | "custom_site_added"
  | "subscription_fixed"
  | "reconciliation_run"
  | "user_email_changed"
  | "account_deletion_completed"
  | "account_deletion_cancelled"
  // CRM
  | "contact_created"
  | "contact_updated"
  | "contact_deleted"
  | "contact_comm_logged";

type AdminAction =
  | "load_health"
  | "load_ads_attribution"
  | "load_signup_attribution"
  | "load_signup_funnel"
  | "load_audit_log"
  | "search_users"
  | "impersonate_session"
  | "impersonate_link"
  | "add_custom_site"
  | "fix_subscription"
  | "run_reconciliation"
  | "change_email"
  | "complete_account_deletion"
  | "cancel_account_deletion"
  | "load_contacts"
  | "create_contact"
  | "update_contact"
  | "delete_contact"
  | "add_contact_comm";

type AdminAttributes = Record<string, string | number | boolean | null | undefined>;

/**
 * The author rides in `attributes`, not `ObservabilityScope` — `logFeatureEvent`
 * only flattens the property axis (`property_id`, `domain`, …) and drops
 * `userId`/`email`, which exist purely for `setUser` on Issues.
 */
const authorAttributes = (author: AdminAuthor): AdminAttributes => ({
  admin_user_id: author.userId ?? null,
  admin_email: author.email ?? null,
  admin_username: author.adminUsername ?? null,
  admin_session_id: getAdminAuditSessionId(),
  occurred_at: new Date().toISOString(),
});

const persistAdminAudit = (
  event: AdminEvent,
  author: AdminAuthor,
  attributes?: AdminAttributes,
) => {
  const occurredAt =
    typeof attributes?.occurred_at === "string"
      ? attributes.occurred_at
      : new Date().toISOString();
  const sessionId =
    typeof attributes?.admin_session_id === "string"
      ? attributes.admin_session_id
      : getAdminAuditSessionId();

  recordAdminAudit({
    event,
    occurred_at: occurredAt,
    admin_session_id: sessionId,
    admin_user_id: author.userId ?? null,
    admin_email: author.email ?? null,
    admin_username: author.adminUsername ?? null,
    admin_name: author.name ?? null,
    target_user_id: typeof attributes?.target_user_id === "string" ? attributes.target_user_id : null,
    target_email: typeof attributes?.target_email === "string" ? attributes.target_email : null,
    attributes,
  });
};

const STATUS_IN_MESSAGE = /\b(401|403)\b/;
const DENIED_IN_MESSAGE = /unauthorized|forbidden|invalid credentials|not authori[sz]ed/i;

/**
 * A wrong `ADMIN_API_KEY` is the expected outcome of a typo, not a defect, so it
 * stays a Log — but it is the one Log the security review actually cares about.
 *
 * The admin helpers in `api.ts` throw bare `Error`s ("admin/health HTTP 401",
 * "HTTP error! status: 403", or the backend's own error string), so
 * `getHttpStatus` has nothing structured to read and the message is the only
 * signal available. Without this fallback every mistyped key would open an Issue,
 * which our Sentry-fix automation would then raise a PR against.
 */
const accessDeniedStatus = (error: unknown): number | null => {
  const status = getHttpStatus(error);
  if (status !== null) return status === 401 || status === 403 ? status : null;

  const message = getErrorMessage(error);
  const matched = message.match(STATUS_IN_MESSAGE);
  if (matched) return Number(matched[1]);

  return DENIED_IN_MESSAGE.test(message) ? 401 : null;
};

export const logAdminEvent = (
  event: AdminEvent,
  author: AdminAuthor,
  attributes?: AdminAttributes,
) => {
  const payload = { ...authorAttributes(author), ...attributes };
  logFeatureEvent({
    feature: "admin",
    event,
    message: `admin: ${event}`,
    attributes: payload,
  });
  persistAdminAudit(event, author, payload);
};

const logAdminAccessDenied = (
  action: AdminAction,
  author: AdminAuthor,
  httpStatus: number | null,
  dedupKey?: string,
) => {
  const attributes = {
    admin_action: action,
    http_status: httpStatus ?? null,
  };
  const payload = { ...authorAttributes(author), ...attributes };
  logFeatureEvent({
    feature: "admin",
    event: "access_denied",
    level: "warn",
    message: `admin: access denied on ${action}`,
    attributes: payload,
    dedupKey,
  });
  persistAdminAudit("access_denied", author, payload);
};

export const reportAdminFailure = (
  action: AdminAction,
  error: unknown,
  author: AdminAuthor,
  attributes?: AdminAttributes,
  /** Set for polled/refetched calls so one broken query doesn't flood Sentry. */
  dedupKey?: string,
) => {
  const deniedStatus = accessDeniedStatus(error);

  if (deniedStatus !== null) {
    logAdminAccessDenied(action, author, deniedStatus, dedupKey);
    return;
  }

  // Response-less browser/network failures (TypeError: Failed to fetch,
  // AbortError, NetworkError) are transient client or upstream-drop conditions.
  // Attribution and analytics already log these as `load_degraded`; HTTP 5xx
  // and parse failures still open Issues.
  if (isNetworkFailure(error)) {
    const payload = {
      ...authorAttributes(author),
      admin_action: action,
      error_message: getErrorMessage(error),
      ...attributes,
    };
    logFeatureEvent({
      feature: "admin",
      event: "load_degraded",
      level: "warn",
      message: `admin: ${action} load degraded`,
      attributes: payload,
      dedupKey,
    });
    return;
  }

  reportFeatureIssue({
    feature: "admin",
    error,
    action,
    message: `Admin portal: ${action} failed`,
    scope: { userId: author.userId, email: author.email },
    tags: { admin_username: author.adminUsername ?? undefined },
    context: { ...authorAttributes(author), ...attributes },
    dedupKey,
  });
};
